import React, { useState, useEffect, useRef } from 'react';
import './styling/ManagerDashboard.css';
import { useNavigate } from 'react-router-dom';
import ContentListItem from '../Components/ContentListItem/ContentListItem';
import ContentForm from '../Components/ContentForm/ContentForm';
import UserListItem from '../Components/UserListItem/UserListItem';
import UserForm from '../Components/UserForm/UserForm';
import SeriesForm from '../Components/SeriesForm/SeriesForm';
import BannerForm from '../Components/BannerForm/BannerForm';
import { useUsers } from '../hooks/useUsers';
import { useContentList } from '../hooks/useContentList';
import { useSeries } from '../hooks/useSeries';
import { ModalMessage } from '../Components/ModalMessage/ModalMessage';
import { useSections } from '../hooks/useSections';
import Sections from './Sections/Sections';
import { useBanners } from '../hooks/useBanners';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { FaHome, FaSignOutAlt, FaUsersCog } from 'react-icons/fa';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import API_ROUTES from '../hooks/apiRoutes';
import { Series } from '../Models/Series';
import TemplateForm from '../Components/TemplateForm';
import TemplateListItem from '../Components/TemplateListItem';
import { useTemplates } from '../hooks/useTemplates';

const ManagerDashboard = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(0);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingContent, setEditingContent] = useState(null);
    const [editingUser, setEditingUser] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [openSectionsForm, setOpenSectionsForm] = useState(false);
    const [openBannerForm, setOpenBannerForm] = useState(false);
    const [editingBanner, setEditingBanner] = useState(null);
    const [showResetModal, setShowResetModal] = useState(false);
    const [userToReset, setUserToReset] = useState(null);

    // Separate search states for each tab
    const [searchText, setSearchText] = useState({
        sections: '',
        series: '',
        contents: '',
        users: '',
        templates: '',
    });

    // PAGINAÇÃO USUÁRIOS
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    // Using hooks with lazy initialization
    const {
        users,
        loading: usersLoading,
        error: usersError,
        success,
        totalCount,
        fetchUsers,
        deleteUser,
        updateUser,
        createUser
    } = useUsers();

    const {
        contents,
        loading: contentsLoading,
        error: contentsError,
        createContent,
        updateContent,
        deleteContent,
        refreshContents
    } = useContentList();

    const {
        series,
        loading: seriesLoading,
        error: seriesError,
        createSeries,
        updateSeries,
        deleteSeries,
        refreshSeries,
        fetchSerieWithEpisodes
    } = useSeries();

    const {
        sections,
        loading: sectionsLoading,
        error: sectionsError,
        createSection,
        updateSection,
        deleteSection,
        sortSections,
        refreshSections
    } = useSections();

    const {
        banners,
        loading: bannersLoading,
        error: bannersError,
        fetchBanners,
        createBanner,
        updateBanner,
        deleteBanner
    } = useBanners();

    // Templates
    const {
        templates,
        loading: templatesLoading,
        error: templatesError,
        fetchTemplates,
        createTemplate,
        updateTemplate,
        deleteTemplate,
        selectedTemplate,
        setSelectedTemplate,
        success: templatesSuccess,
        resetStates
    } = useTemplates();
    const [openTemplateForm, setOpenTemplateForm] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [showDeleteTemplateModal, setShowDeleteTemplateModal] = useState(false);
    const [templateToDelete, setTemplateToDelete] = useState(null);

    // Fetch data only when respective tab is selected
    useEffect(() => {
        if (activeTab === 0) { // Sections tab
            refreshSections();
        } else if (activeTab === 1) { // Series tab
            refreshSeries();
        } else if (activeTab === 2) { // Contents tab
            refreshContents();
        } else if (activeTab === 3) { // Users tab
            fetchUsers();
        } else if (activeTab === 4) { // Banners tab
            fetchBanners();
        } else if (activeTab === 5) { // Templates tab
            fetchTemplates();
        }
    }, [activeTab, refreshSections, refreshSeries, refreshContents, fetchUsers, fetchBanners, fetchTemplates]);

    // Effect to close modal when operation is successful
    useEffect(() => {
        if (success && isFormOpen) {
            setIsFormOpen(false);
            setEditingContent(null);
            setEditingUser(null);
        }
    }, [success]);

    // Effect to refresh contents when form opens
    useEffect(() => {
        if (isFormOpen && activeTab === 1) { // Series tab
            refreshSeries();
        }
    }, [isFormOpen, activeTab]);

    // Temporary mock data - replace with API calls
    const [playlists] = useState([
        {
            id: 1,
            title: 'Playlist de Exemplo',
            description: 'Uma playlist de demonstração',
            thumbnailUrl: 'https://picsum.photos/400/300'
        },
        {
            id: 2,
            title: 'Playlist de Exemplo',
            description: 'Uma playlist de demonstração',
            thumbnailUrl: 'https://picsum.photos/400/300'
        },
        {
            id: 3,
            title: 'Playlist de Exemplo',
            description: 'Uma playlist de demonstração',
            thumbnailUrl: 'https://picsum.photos/400/300'
        }, {
            id: 4,
            title: 'Playlist de Exemplo',
            description: 'Uma playlist de demonstração',
            thumbnailUrl: 'https://picsum.photos/400/300'
        },
        {
            id: 5,
            title: 'Playlist de Exemplo',
            description: 'Uma playlist de demonstração',
            thumbnailUrl: 'https://picsum.photos/400/300'
        }
    ]);

    const handleTabChange = (index) => {
        setActiveTab(index);
    };

    const handleAddButtonClick = async () => {
        switch (activeTab) {
            case 0:
                setOpenSectionsForm(true);
                break;
            case 1:
                await refreshContents();
                setIsFormOpen(true);
                break;
            case 2:
                setIsFormOpen(true);
                break;
            case 3:
                setIsFormOpen(true);
                break;
            case 4:
                setOpenBannerForm(true);
                break;
            case 5:
                resetStates();
                setEditingTemplate(null);
                setIsFormOpen(true);
                break;
            default:
                break;
        }
    };

    const handleEditContent = async (content) => {
        if (activeTab === 1) {
            await refreshContents();
            // Buscar a série completa com episódios
            const fullSerie = await fetchSerieWithEpisodes(content.id);
            let serieObj = fullSerie && fullSerie.serie ? fullSerie.serie : null;
            if (serieObj && serieObj.serie) {
                serieObj = serieObj.serie;
            }
            setEditingContent(serieObj ? Series.fromAPI(serieObj) : content);
        } else {
            setEditingContent(content);
        }
        setEditingUser(null);
        setIsFormOpen(true);
    };

    const handleEditUser = (user) => {
        setEditingUser(user);
        setEditingContent(null);
        setIsFormOpen(true);
    };

    const handleDeleteContent = async (content) => {
        try {
            if (activeTab === 1) { // Series tab
                await deleteSeries(content.id);
            } else {
                await deleteContent(content.id);
            }
        } catch (error) {
            console.error('Erro ao deletar conteúdo:', error);
        }
    };

    const handleDeleteUserClick = (user) => {
        setUserToDelete(user);
        setShowDeleteModal(true);
    };

    const confirmDeleteUser = async () => {
        if (userToDelete) {
            await deleteUser(userToDelete.user_id);
            setShowDeleteModal(false);
            setUserToDelete(null);
        }
    };

    const handleFormSubmit = async (formData) => {
        if (activeTab === 3) { // Users tab
            try {
                if (editingUser) {
                    await updateUser(editingUser.user_id, formData);
                } else {
                    await createUser(formData);
                }
            } catch (error) {
                console.error('Erro ao salvar usuário:', error);
            }
        } else if (activeTab === 1) { // Series tab
            try {
                if (editingContent) {
                    await updateSeries(formData.id, formData);
                } else {
                    await createSeries(formData);
                }
                setIsFormOpen(false);
                setEditingContent(null);
            } catch (error) {
                console.error('Erro ao salvar série:', error);
            }
        } else if (activeTab === 2) { // Contents tab
            try {
                if (editingContent) {
                    await updateContent(formData.id, formData);
                } else {
                    await createContent(formData);
                }
        setIsFormOpen(false);
        setEditingContent(null);
            } catch (error) {
                console.error('Erro ao salvar conteúdo:', error);
            }
        }
    };

    const handleUserRoleClick = async (user, subscription) => {
        try {
            const updatedSubscriptions = (user.user_subscriptions || []).includes(subscription)
                ? user.user_subscriptions.filter(s => s !== subscription)
                : [...(user.user_subscriptions || []), subscription];

            await updateUser(user.user_id, { ...user, user_subscriptions: updatedSubscriptions });
        } catch (error) {
            console.error('Erro ao atualizar função do usuário:', error);
        }
    };

    const getCurrentType = () => {
        switch (activeTab) {
            case 0: return 'playlist';
            case 1: return 'serie';
            case 2: return 'video';
            case 3: return 'user';
            default: return 'video';
        }
    };

    const getCurrentItems = () => {
        const query = searchText[Object.keys(searchText)[activeTab]].toLowerCase();

        if (activeTab === 3) { // Users tab
            return users.filter(user =>
                user.user_name?.toLowerCase().includes(query) ||
                user.user_email?.toLowerCase().includes(query)
            );
        } else if (activeTab === 1) { // Series tab
            return series.filter(serie =>
                serie.title?.toLowerCase().includes(query) ||
                serie.description?.toLowerCase().includes(query)
            );
        } else if (activeTab === 2) { // Contents tab
            return contents.filter(content =>
                content.title?.toLowerCase().includes(query) ||
                content.description?.toLowerCase().includes(query)
            );
        }
        return [];
    };

    const handleSearchChange = (tab, value) => {
        setSearchText(prev => ({
            ...prev,
            [tab]: value
        }));
    };

    const getSearchPlaceholder = () => {
        switch (activeTab) {
            case 0: return "Buscar seções";
            case 1: return "Buscar séries";
            case 2: return "Buscar conteúdos";
            case 3: return "Buscar usuários";
            case 5: return "Buscar templates";
            default: return "";
        }
    };

    const getSearchKey = () => {
        switch (activeTab) {
            case 0: return 'sections';
            case 1: return 'series';
            case 2: return 'contents';
            case 3: return 'users';
            case 5: return 'templates';
            default: return '';
        }
    };

    useEffect(() => {
        if (activeTab === 3) {
            fetchUsers({
                search: searchText['users'],
                page: currentPage,
                size: pageSize
            });
        }
    }, [activeTab, currentPage, searchText['users']]);

    // Resetar página ao trocar de aba ou busca
    useEffect(() => {
        if (activeTab === 3) setCurrentPage(1);
    }, [activeTab, searchText['users']]);

    const handleLogout = () => {
        setShowLogoutModal(true);
    };

    const confirmLogout = () => {
        localStorage.removeItem('token');
        navigate('/login');
    };

    const renderContent = () => {
        switch (activeTab) {
            case 0:
                return <Sections 
                    sections={sections}
                    loading={sectionsLoading}
                    error={sectionsError}
                    createSection={createSection}
                    updateSection={updateSection}
                    deleteSection={deleteSection}
                    sortSections={sortSections}
                    refreshSections={refreshSections}
                    openSectionsForm={openSectionsForm}
                    setOpenSectionsForm={setOpenSectionsForm}
                />;
            case 1:
                return renderSeriesTab();
            case 2:
                return renderContentsTab();
            case 3:
                return renderUsersTab();
            case 4:
                return renderBannersTab();
            default:
                return null;
        }
    };

    const renderSeriesTab = () => {
        const isLoading = seriesLoading;
        const error = seriesError;

        if (isLoading) {
            return (
                <div className="loading-container">
                    <div className="spinner"></div>
                    <span>Carregando...</span>
                </div>
            );
        }

        if (error) {
            return (
                <div className="error-message">
                    {error}
                </div>
            );
        }

        return (
            <>
                {getCurrentItems().map((item) => (
                    <ContentListItem
                        key={item.id}
                        title={item.title}
                        description={item.description}
                        thumbnailUrl={item.thumbnailUrl}
                        onEdit={() => handleEditContent(item)}
                        onDelete={() => handleDeleteContent(item)}
                        isDraggable={activeTab === 0}
                    />
                ))}
            </>
        );
    };

    const renderContentsTab = () => {
        const isLoading = contentsLoading;
        const error = contentsError;

        if (isLoading) {
            return (
                <div className="loading-container">
                    <div className="spinner"></div>
                    <span>Carregando...</span>
                </div>
            );
        }

        if (error) {
            return (
                <div className="error-message">
                    {error}
                </div>
            );
        }

        return (
            <>
                {getCurrentItems().map((item) => (
            <ContentListItem
                key={item.id}
                title={item.title}
                description={item.description}
                thumbnailUrl={item.thumbnailUrl}
                status={item.status}
                onEdit={() => handleEditContent(item)}
                onDelete={() => handleDeleteContent(item)}
                isDraggable={activeTab === 0}
            />
                ))}
            </>
        );
    };

    const renderUsersTab = () => {
        const isLoading = usersLoading;
        const error = usersError;

        if (isLoading) {
            return (
                <div className="loading-container">
                    <div className="spinner"></div>
                    <span>Carregando...</span>
                </div>
            );
        }

        if (error) {
            return (
                <div className="error-message">
                    {error}
                </div>
            );
        }

        return (
            <>
                {getCurrentItems().map((item) => (
                    <UserListItem
                        key={item.user_id}
                        name={item.user_name}
                        email={item.user_email}
                        roles={item.user_subscriptions}
                        onEdit={() => handleEditUser(item)}
                        onDelete={() => handleDeleteUserClick(item)}
                        onResetPassword={() => {
                            setUserToReset(item);
                            setShowResetModal(true);
                        }}
                    />
                ))}
            </>
        );
    };

    const renderBannersTab = () => {
    return (
            <div>
                <h3>Banners cadastrados:</h3>
                {bannersLoading ? (
                    <div>Carregando...</div>
                ) : bannersError ? (
                    <div>{bannersError}</div>
                ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                        {banners.filter(banner => banner.banner_url && banner.banner_url.trim() !== '').map(banner => (
                            <div key={banner.id || banner.banner_id} style={{ position: 'relative', width: 200 }}>
                                <img 
                                    src={banner.banner_url} 
                                    alt="Banner" 
                                    style={{ 
                                        width: 200, 
                                        borderRadius: 8,
                                        cursor: 'pointer',
                                        transition: 'opacity 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                                    onClick={() => {
                                        setEditingBanner(banner);
                                        setOpenBannerForm(true);
                                    }}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                    }}
                                />
                                <div 
                                    style={{ 
                                        width: 200, 
                                        height: 150, 
                                        borderRadius: 8, 
                                        background: '#333', 
                                        display: 'none',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#999',
                                        fontSize: 14,
                                        textAlign: 'center',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => {
                                        setEditingBanner(banner);
                                        setOpenBannerForm(true);
                                    }}
                                >
                                    Imagem não encontrada
                                </div>
            <button
                                    onClick={() => {
                                        setEditingBanner(banner);
                                        setOpenBannerForm(true);
                                    }}
                                    style={{
                                        position: 'absolute',
                                        top: 6,
                                        left: 6,
                                        background: 'rgba(0,0,0,0.7)',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: 28,
                                        height: 28,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        color: '#fff',
                                        zIndex: 2,
                                        transition: 'background-color 0.2s'
                                    }}
                                    title="Editar banner"
                                    onMouseEnter={(e) => e.target.style.background = 'rgba(0,0,0,0.9)'}
                                    onMouseLeave={(e) => e.target.style.background = 'rgba(0,0,0,0.7)'}
                                >
                                    <EditIcon style={{ fontSize: 18 }} />
            </button>
                                <button
                                    onClick={() => deleteBanner(banner.id || banner.banner_id)}
                                    style={{
                                        position: 'absolute',
                                        top: 6,
                                        right: 6,
                                        background: 'rgba(227,26,19,0.85)',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: 28,
                                        height: 28,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        color: '#fff',
                                        zIndex: 2
                                    }}
                                    title="Deletar banner"
                                >
                                    <DeleteIcon style={{ fontSize: 18 }} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                </div>
        );
    };

    const renderTemplatesTab = () => {
        if (templatesLoading) {
            return (
                <div className="loading-container">
                    <div className="spinner"></div>
                    <span>Carregando...</span>
                </div>
            );
        }
        if (templatesError) {
            return <div className="error-message">{templatesError}</div>;
        }
        return (
            <>
                {templates && templates.length > 0 ? (
                    templates.map((template) => (
                        <TemplateListItem
                            key={template.template_id || template.id}
                            template={template}
                            onEdit={() => handleEditTemplate(template)}
                            onDelete={() => handleDeleteTemplateClick(template)}
                        />
                    ))
                ) : (
                    <div style={{ color: '#999', textAlign: 'center', marginTop: 32 }}>
                        Nenhum template cadastrado.
                    </div>
                )}
            </>
        );
    };

    const renderForm = () => {
        if (activeTab === 3) { // Users tab
            return (
                    <UserForm
                        open={isFormOpen}
                        onClose={() => {
                            setIsFormOpen(false);
                            setEditingUser(null);
                        }}
                        onSubmit={handleFormSubmit}
                        initialData={editingUser}
                    />
            );
        } else if (activeTab === 1) { // Series tab
            return (
                <SeriesForm
                    open={isFormOpen}
                    onClose={() => {
                        setIsFormOpen(false);
                        setEditingContent(null);
                    }}
                    onSubmit={handleFormSubmit}
                    initialData={editingContent}
                    availableVideos={contents}
                />
            );
        } else if (activeTab === 2) { // conteudos tab
            return (
                <ContentForm
                    open={isFormOpen}
                    onClose={() => {
                        setIsFormOpen(false);
                        setEditingContent(null);
                    }}
                    onSubmit={handleFormSubmit}
                    initialData={editingContent}
                />
            );
        } else if (activeTab === 4) { // Banners tab
            return (
                <BannerForm
                    open={openBannerForm}
                    onClose={() => {
                        setOpenBannerForm(false);
                        setEditingBanner(null);
                    }}
                    onSubmit={handleBannerSubmit}
                    initialData={editingBanner}
                />
            );
        } else if (activeTab === 5 && isFormOpen) {
            return (
                <TemplateForm
                    open={isFormOpen}
                    onClose={() => {
                        setIsFormOpen(false);
                        setEditingTemplate(null);
                    }}
                    onSubmit={handleTemplateFormSubmit}
                    initialData={editingTemplate || { template_name: '', template_html: '' }}
                />
            );
        } else {
            return (
                    <ContentForm
                        open={isFormOpen}
                        onClose={() => {
                            setIsFormOpen(false);
                            setEditingContent(null);
                        }}
                        onSubmit={handleFormSubmit}
                        initialData={editingContent}
                        type={getCurrentType()}
                    />
            );
        }
    };

    const tabs = ['Seções', 'Séries', 'Conteúdos', 'Usuários', 'Banners', 'Templates'];

    useEffect(() => {
        if (templatesSuccess && isFormOpen && activeTab === 5) {
            setIsFormOpen(false);
            setEditingTemplate(null);
        }
    }, [templatesSuccess, isFormOpen, activeTab]);

    const handleBannerSubmit = async (bannerData) => {
        try {
            if (editingBanner) {
                await updateBanner(editingBanner.banner_id || editingBanner.id, bannerData);
            } else {
                await createBanner(bannerData);
            }
            setOpenBannerForm(false);
            setEditingBanner(null);
        } catch (error) {
            console.error('Erro ao salvar banner:', error);
        }
    };

    const handleEditTemplate = (template) => {
        resetStates();
        setEditingTemplate(template);
        setIsFormOpen(true);
    };
    const handleDeleteTemplateClick = (template) => {
        setTemplateToDelete(template);
        setShowDeleteTemplateModal(true);
    };
    const confirmDeleteTemplate = async () => {
        if (templateToDelete) {
            await deleteTemplate(templateToDelete.template_id || templateToDelete.id);
            setShowDeleteTemplateModal(false);
            setTemplateToDelete(null);
        }
    };
    const handleTemplateFormSubmit = async (formData) => {
        if (editingTemplate) {
            await updateTemplate(editingTemplate.template_id || editingTemplate.id, formData);
        } else {
            await createTemplate(formData);
        }
    };

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

    const handleResetPassword = async (email) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(API_ROUTES.FORGOT_PASSWORD.defaultReset(), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email })
            });
            if (!response.ok) throw new Error('Erro ao resetar senha');
            setSnackbar({ open: true, message: 'Senha resetada com sucesso!', severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: err.message || 'Erro ao resetar senha', severity: 'error' });
        }
    };

    return (
        <div className="manager-dashboard">
            <header className="dashboard-header">
                <div className="header-content">
                    <div className="header-left">
                        <img
                            src="/imgs/reino_header_logo.svg"
                            alt="Reino Play Logo"
                            className="dashboard-logo"
                        />
                    </div>
                    <div className="header-right">
                        {/* A ponte para o painel novo. Os dois convivem DE PROPÓSITO: este aqui
                            funciona e a equipe depende dele todo dia. Quem quiser experimentar o
                            novo clica aqui — e volta quando quiser. O antigo só sai depois que a
                            equipe confirmar que não perdeu nada. */}
                        <button
                            className="header-button"
                            onClick={() => navigate("/admin")}
                            title="Experimentar o painel novo — você pode voltar quando quiser"
                            style={{
                                width: 'auto',
                                gap: 8,
                                paddingInline: 14,
                                background: '#E50914',
                                color: '#fff',
                                fontWeight: 600,
                            }}
                        >
                            <span style={{ fontSize: 14 }}>Novo painel</span>
                            <span
                                style={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    background: 'rgba(255,255,255,.22)',
                                    borderRadius: 5,
                                    padding: '1px 5px',
                                }}
                            >
                                v2
                            </span>
                        </button>
                        <button
                            className="header-button"
                            onClick={() => navigate("/admin/assinaturas")}
                            title="Usuários & Assinaturas"
                            style={{ width: 'auto', gap: 8, paddingInline: 14 }}
                        >
                            <FaUsersCog size={18} />
                            <span style={{ fontSize: 14 }}>Assinaturas</span>
                        </button>
                        <button className="header-button" onClick={() => navigate("/")}>
                            <FaHome size={20} />
                        </button>
                        <button className="header-button" onClick={handleLogout}>
                            <FaSignOutAlt size={20} />
                        </button>
                    </div>
                </div>
            </header>

            <main className="dashboard-content">
                <nav className="tabs-container">
                    {tabs.map((tab, index) => (
                        <button
                            key={tab}
                            className={`tab-button ${activeTab === index ? 'active' : ''}`}
                            onClick={() => handleTabChange(index)}
                        >
                            {tab}
                        </button>
                    ))}
                </nav>

                <div className="action-buttons">
                    <button
                        className="action-button"
                        onClick={handleAddButtonClick}
                    >
                        {activeTab === 3 ? "Novo Usuário" :
                            activeTab === 0 ? "Nova Seção" :
                                activeTab === 1 ? "Nova Série" :
                                    activeTab === 2 ? "Novo Conteúdo" :
                                        activeTab === 4 ? "Novo Banner" :
                                            activeTab === 5 ? "Novo Template" :
                                                "Novo Usuário"}
                    </button>
                </div>

                {activeTab !== 4 && (
                    <div className="search-container">
                        <input
                            type="text"
                            className="manager-input"
                            placeholder={getSearchPlaceholder()}
                            value={searchText[getSearchKey()]}
                            onChange={(e) => handleSearchChange(getSearchKey(), e.target.value)}
                        />
                    </div>
                )}

                <div className="content-list">
                    {activeTab === 5 ? renderTemplatesTab() : renderContent()}
                </div>

                {/* PAGINAÇÃO USUÁRIOS */}
                {activeTab === 3 && totalCount > 0 && (
                  <div className="pagination" style={{ display: 'flex', justifyContent: 'center', margin: '16px 0', gap: 4 }}>
                    {/* Botão Anterior */}
                    {currentPage > 1 && (
                      <button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        style={{
                          padding: '6px 14px',
                          borderRadius: 4,
                          border: '1px solid #222',
                          background: '#111111',
                          color: '#fff',
                          fontWeight: 500,
                          cursor: 'pointer'
                        }}
                      >
                        Anterior
                      </button>
                    )}
                    {/* Números das páginas (máximo 3) */}
                    {(() => {
                      // Calcular número total de páginas baseado no count real da API
                      const totalPages = Math.ceil(totalCount / pageSize);
                      let start = Math.max(1, currentPage - 1);
                      let end = Math.min(totalPages, start + 2);
                      if (end - start < 2) start = Math.max(1, end - 2);
                      const pages = [];
                      for (let page = start; page <= end; page++) {
                        pages.push(
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            style={{
                              margin: '0 2px',
                              padding: '6px 14px',
                              borderRadius: 4,
                              border: page === currentPage ? '2px solid #d32f2f' : '1px solid #222',
                              background: page === currentPage ? '#d32f2f' : '#111111',
                              color: '#fff',
                              fontWeight: page === currentPage ? 600 : 400,
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            disabled={page === currentPage}
                          >
                            {page}
                          </button>
                        );
                      }
                      return pages;
                    })()}
                    {/* Botão Próxima */}
                    {currentPage < Math.ceil(totalCount / pageSize) && (
                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        style={{
                          padding: '6px 14px',
                          borderRadius: 4,
                          border: '1px solid #222',
                          background: '#111111',
                          color: '#fff',
                          fontWeight: 500,
                          cursor: 'pointer'
                        }}
                      >
                        Próxima
                      </button>
                    )}
                  </div>
                )}

                {renderForm()}
            </main>

            {showDeleteModal && (
                <ModalMessage
                    title="Tem certeza?"
                    description="Deseja realmente deletar este usuário?"
                    leftButtonText="Sim"
                    leftButtonAction={confirmDeleteUser}
                    rightButtonText="Não"
                    rightButtonAction={() => setShowDeleteModal(false)}
                />
            )}

            {showLogoutModal && (
                <ModalMessage
                    title="Tem certeza?"
                    description="Deseja realmente sair da sua conta?"
                    leftButtonText="Sim"
                    leftButtonAction={confirmLogout}
                    rightButtonText="Não"
                    rightButtonAction={() => setShowLogoutModal(false)}
                />
            )}

            {showResetModal && (
                <ModalMessage
                    title="Tem certeza?"
                    description={`Deseja realmente resetar a senha do usuário ${userToReset?.user_email}?`}
                    leftButtonText="Sim"
                    leftButtonAction={async () => {
                        await handleResetPassword(userToReset.user_email);
                        setShowResetModal(false);
                        setUserToReset(null);
                    }}
                    rightButtonText="Não"
                    rightButtonAction={() => {
                        setShowResetModal(false);
                        setUserToReset(null);
                    }}
                />
            )}

            {showDeleteTemplateModal && (
                <ModalMessage
                    title="Tem certeza?"
                    description="Deseja realmente deletar este template?"
                    leftButtonText="Sim"
                    leftButtonAction={confirmDeleteTemplate}
                    rightButtonText="Não"
                    rightButtonAction={() => setShowDeleteTemplateModal(false)}
                />
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
            >
                <MuiAlert onClose={handleSnackbarClose} severity={snackbar.severity}>
                    {snackbar.message}
                </MuiAlert>
            </Snackbar>
        </div>
    );
};

export default ManagerDashboard; 